import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Minus, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const PREVIEW_SIZE = 280;
const OUTPUT_SIZE = 720;

function canvasToBlob(canvas, type = 'image/webp', quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Nao foi possivel preparar a foto.'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function getSafeName(file) {
  return String(file?.name || 'foto-perfil')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-') || 'foto-perfil';
}

function clampOffset(nextOffset, imageSize) {
  const clampAxis = (value, renderedSize) => {
    if (renderedSize <= PREVIEW_SIZE) return (PREVIEW_SIZE - renderedSize) / 2;
    return Math.min(0, Math.max(PREVIEW_SIZE - renderedSize, value));
  };

  return {
    x: clampAxis(nextOffset.x, imageSize.width),
    y: clampAxis(nextOffset.y, imageSize.height),
  };
}

export default function ProfilePhotoCropper({ file, open, onCancel, onConfirm, isProcessing = false }) {
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!file || !open) return undefined;

    const objectUrl = URL.createObjectURL(file);
    setImageUrl(objectUrl);
    setZoom(1);
    setOffset({ x: 0, y: 0 });

    return () => URL.revokeObjectURL(objectUrl);
  }, [file, open]);

  const baseScale = useMemo(() => {
    if (!imageSize.width || !imageSize.height) return 1;
    return Math.max(PREVIEW_SIZE / imageSize.width, PREVIEW_SIZE / imageSize.height);
  }, [imageSize]);

  const renderedSize = useMemo(() => ({
    width: imageSize.width * baseScale * zoom,
    height: imageSize.height * baseScale * zoom,
  }), [baseScale, imageSize.height, imageSize.width, zoom]);

  const resetPosition = useCallback(() => {
    const size = {
      width: imageSize.width * baseScale,
      height: imageSize.height * baseScale,
    };
    setZoom(1);
    setOffset(clampOffset({
      x: (PREVIEW_SIZE - size.width) / 2,
      y: (PREVIEW_SIZE - size.height) / 2,
    }, size));
  }, [baseScale, imageSize.height, imageSize.width]);

  const handleImageLoad = (event) => {
    const image = event.currentTarget;
    const nextSize = {
      width: image.naturalWidth || 1,
      height: image.naturalHeight || 1,
    };
    const nextBaseScale = Math.max(PREVIEW_SIZE / nextSize.width, PREVIEW_SIZE / nextSize.height);
    const nextRenderedSize = {
      width: nextSize.width * nextBaseScale,
      height: nextSize.height * nextBaseScale,
    };

    setImageSize(nextSize);
    setZoom(1);
    setOffset(clampOffset({
      x: (PREVIEW_SIZE - nextRenderedSize.width) / 2,
      y: (PREVIEW_SIZE - nextRenderedSize.height) / 2,
    }, nextRenderedSize));
  };

  const handlePointerDown = (event) => {
    if (isProcessing) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offset,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const nextOffset = {
      x: drag.offset.x + event.clientX - drag.startX,
      y: drag.offset.y + event.clientY - drag.startY,
    };
    setOffset(clampOffset(nextOffset, renderedSize));
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const handleZoomChange = (event) => {
    const nextZoom = Number(event.target.value);
    const center = {
      x: PREVIEW_SIZE / 2,
      y: PREVIEW_SIZE / 2,
    };
    const ratio = nextZoom / zoom;
    const nextRenderedSize = {
      width: imageSize.width * baseScale * nextZoom,
      height: imageSize.height * baseScale * nextZoom,
    };
    const nextOffset = {
      x: center.x - (center.x - offset.x) * ratio,
      y: center.y - (center.y - offset.y) * ratio,
    };

    setZoom(nextZoom);
    setOffset(clampOffset(nextOffset, nextRenderedSize));
  };

  const handleConfirm = async () => {
    if (!imageRef.current || !file) return;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Nao foi possivel preparar a foto.');

    const outputScale = OUTPUT_SIZE / PREVIEW_SIZE;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    context.drawImage(
      imageRef.current,
      offset.x * outputScale,
      offset.y * outputScale,
      renderedSize.width * outputScale,
      renderedSize.height * outputScale
    );

    const blob = await canvasToBlob(canvas);
    const croppedFile = new File([blob], `${getSafeName(file)}-perfil.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
    await onConfirm(croppedFile);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isProcessing && onCancel?.()}>
      <DialogContent className="max-w-[360px] rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pr-12">
          <DialogTitle className="text-base">Ajustar foto</DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-4 space-y-4">
          <div className="flex justify-center">
            <div
              className="relative bg-black overflow-hidden select-none"
              style={{
                width: PREVIEW_SIZE,
                height: PREVIEW_SIZE,
                borderRadius: '9999px',
                touchAction: 'none',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              {imageUrl ? (
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Foto selecionada"
                  onLoad={handleImageLoad}
                  draggable={false}
                  className="absolute max-w-none touch-none"
                  style={{
                    width: renderedSize.width,
                    height: renderedSize.height,
                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Camera className="w-10 h-10 text-white/60" />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Minus className="w-4 h-4 text-gray-500" />
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={handleZoomChange}
              disabled={isProcessing}
              className="w-full accent-indigo-600"
            />
            <Plus className="w-4 h-4 text-gray-500" />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={resetPosition} disabled={isProcessing}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reposicionar
            </Button>
            <Button type="button" className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleConfirm} disabled={isProcessing}>
              {isProcessing ? 'Enviando...' : 'Usar foto'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
